/// WorkerHost: central routing and request/response with correlation_id + timeout (COMM-02, COMM-04).
/// Extended with event subscription for engine event forwarding (ENGN-03).
// Canonical type definitions: app/ui/src/protocol/types.ts (WorkerLike, EventListener)

import {
  decodeEnvelope,
  encodeEnvelope,
  type MessageEnvelope,
} from "./MessageEnvelope.js";

export type WorkerLike = Pick<Worker, "postMessage" | "onmessage">;

/** Callback signature for event listeners. */
export type EventListener = (workerId: string, env: MessageEnvelope) => void;

interface PendingEntry {
  resolve: (env: MessageEnvelope) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Centralizes routing between workers and UI.
 * - Map workerId -> Worker
 * - sendRequest(workerId, envelope, timeoutMs): Promise<MessageEnvelope>
 * - correlation_id + timeout; pending map resolves/rejects correctly
 * - Event subscription: addEventListener(type, callback) + addGeneralListener(callback)
 */
export class WorkerHost {
  private workers = new Map<string, WorkerLike>();
  private pending = new Map<string, PendingEntry>();
  private eventListeners = new Map<string, Set<EventListener>>();
  private generalListeners = new Set<EventListener>();

  /** Register a worker by id. */
  register(workerId: string, worker: WorkerLike): void {
    if (this.workers.has(workerId)) {
      throw new Error(`WorkerHost: worker "${workerId}" already registered`);
    }
    this.workers.set(workerId, worker);
    worker.onmessage = (e: MessageEvent) => this.handleWorkerMessage(workerId, e);
  }

  /** Send request and wait for response with correlation_id and timeout. */
  sendRequest(
    workerId: string,
    envelope: Omit<MessageEnvelope, "correlation_id">,
    timeoutMs = 5000
  ): Promise<MessageEnvelope> {
    const worker = this.workers.get(workerId);
    if (!worker) {
      return Promise.reject(new Error(`WorkerHost: worker "${workerId}" not registered`));
    }

    const correlation_id = crypto.randomUUID();
    const fullEnvelope: MessageEnvelope = {
      ...envelope,
      correlation_id,
    };

    return new Promise<MessageEnvelope>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(correlation_id)) {
          reject(new Error(`WorkerHost: request timeout after ${timeoutMs}ms`));
        }
      }, timeoutMs);

      this.pending.set(correlation_id, { resolve, reject, timer });

      const encoded = encodeEnvelope(fullEnvelope);
      const buf = encoded.buffer.slice(
        encoded.byteOffset,
        encoded.byteOffset + encoded.byteLength
      );
      worker.postMessage({ buffer: buf, correlation_id }, [buf]);
    });
  }

  /** Broadcast envelope to all workers (target "all"). */
  broadcast(envelope: MessageEnvelope): void {
    const encoded = encodeEnvelope(envelope);
    const buf = encoded.buffer.slice(
      encoded.byteOffset,
      encoded.byteOffset + encoded.byteLength
    );
    for (const [, worker] of this.workers) {
      const copy = buf.slice(0);
      worker.postMessage({ buffer: copy, correlation_id: envelope.correlation_id }, [copy]);
    }
  }

  private handleWorkerMessage(workerId: string, e: MessageEvent): void {
    const data = e.data as {
      buffer?: ArrayBuffer;
      correlation_id?: string;
      event?: boolean;
    };
    if (!data?.buffer) return;

    const env = decodeEnvelope(data.buffer);
    const isEvent = data.event === true || env.direction === "evt";

    // If it is an explicit event (event: true marker or direction === "evt"),
    // skip correlation lookup and dispatch directly to event listeners.
    if (isEvent) {
      this.dispatchEvent(workerId, env);
      // Also forward to legacy onUncorrelatedEvent for backward compatibility
      this.onUncorrelatedEvent?.(workerId, env);
      return;
    }

    const cid = env.correlation_id ?? data.correlation_id;

    if (cid) {
      const entry = this.pending.get(cid);
      if (entry) {
        clearTimeout(entry.timer);
        this.pending.delete(cid);
        entry.resolve(env);
        return;
      }
    }

    // Uncorrelated non-event message (e.g. unmatched correlation) - surface to listeners
    this.onUncorrelatedEvent?.(workerId, env);
  }

  /** Dispatch an event envelope to type-specific and general listeners. */
  private dispatchEvent(workerId: string, env: MessageEnvelope): void {
    // Type-specific listeners
    const typeListeners = this.eventListeners.get(env.type);
    if (typeListeners) {
      for (const listener of typeListeners) {
        listener(workerId, env);
      }
    }
    // General listeners (all events)
    for (const listener of this.generalListeners) {
      listener(workerId, env);
    }
  }

  /** Optional handler for events without correlation (ready, probeComplete, etc.) */
  onUncorrelatedEvent?: (workerId: string, env: MessageEnvelope) => void;

  /** Subscribe to events of a specific type from any worker.
   *  Returns an unsubscribe function. */
  addEventListener(eventType: string, listener: EventListener): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType)!.add(listener);
    return () => {
      this.eventListeners.get(eventType)?.delete(listener);
    };
  }

  /** Subscribe to all events from any worker.
   *  Returns an unsubscribe function. */
  addGeneralListener(listener: EventListener): () => void {
    this.generalListeners.add(listener);
    return () => {
      this.generalListeners.delete(listener);
    };
  }

  /** Unregister a worker by id. */
  unregister(workerId: string): void {
    this.workers.delete(workerId);
  }

  /** Get worker by id. */
  getWorker(workerId: string): WorkerLike | undefined {
    return this.workers.get(workerId);
  }

  /** Number of registered workers. */
  get size(): number {
    return this.workers.size;
  }
}
